const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const ENDPOINT = `${SUPABASE_URL}/functions/v1/liveavatar`;

import { createLogger } from "@/lib/frontend-logger";
import { CORRELATION_HEADER, nextCorrelationId } from "@/lib/correlation";
import { appendOp } from "@/lib/opSlug";

const logger = createLogger("liveavatar.client");

async function call<T>(action: string, params: Record<string, unknown> = {}): Promise<T> {
  const correlationId = nextCorrelationId();
  const startedAt = performance.now();
  const res = await fetch(appendOp(ENDPOINT, `liveavatar-${action.replace(/_/g, "-")}`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_KEY,
      authorization: `Bearer ${SUPABASE_KEY}`,
      [CORRELATION_HEADER]: correlationId,
    },
    body: JSON.stringify({ action, ...params }),
  });
  const durationMs = Math.round(performance.now() - startedAt);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (data && (data.error || data.message)) || `liveavatar ${action} failed (${res.status})`;
    logger.error("liveavatar call failed", {
      action, status: res.status, correlationId, durationMs, error: msg,
    });
    throw new Error(msg);
  }
  logger.info("liveavatar call ok", { action, status: res.status, correlationId, durationMs });
  return data as T;
}

export const liveavatar = {
  listAvatars: () => call<{ avatars: any[] }>("list_avatars"),
  /** Custom avatars owned by the LiveAvatar account (org-gated in the UI). */
  listUserAvatars: () => call<{ avatars: any[] }>("list_user_avatars"),
  listLanguages: () => call<{ languages: any[] }>("list_languages"),
  listContexts: () => call<{ contexts: any[] }>("list_contexts"),
  listPublicVoices: () => call<{ voices: any[] }>("list_custom_voices"),
  listPrivateVoices: () => call<{ voices: any[] }>("list_private_voices"),
  listThirdPartyVoices: (secret_id?: string) =>
    call<{ voices: any[]; secret_id: string | null }>("list_third_party_voices", secret_id ? { secret_id } : {}),
  listSecrets: () => call<{ secrets: any[] }>("list_secrets"),
  createContext: (input: { context_name?: string; context_prompt: string; opening_text?: string }) =>
    call<{ context_id: string; context: any }>("create_context", input),
  updateContext: (input: { context_id: string; name?: string; prompt?: string; opening_text?: string }) =>
    call<{ context: any }>("update_context", input),
  deleteContext: (context_id: string) => call<{ ok: true }>("delete_context", { context_id }),
  createSessionToken: (input: {
    avatar_id?: string;
    context_id: string;
    voice_id?: string;
    language?: string;
    llm_configuration_id?: string;
  }) => call<{ session_token: string; context_id: string }>("create_session_token", input),
  startSession: (session_token: string) => call<any>("start_session", { session_token }),
  startByoSession: (input: {
    avatar_id?: string;
    context_id: string;
    voice_id?: string;
    language?: string;
    llm_configuration_id?: string;
    system_prompt?: string;
    opening_text?: string;
  }) =>
    call<{
      livekit_url: string;
      viewer_token: string;
      room_name: string;
      session_id: string | null;
      session_token: string;
      agent_name: string;
      agent_dispatch_id: string | null;
    }>("start_byo_session", input),
  getSession: (session_id: string) => call<Record<string, unknown>>("get_session", { session_id }),
  getSessionTranscript: (session_id: string) =>
    call<{ session_active: boolean; next_timestamp?: number; transcript_data: Array<{ role: "user" | "avatar"; transcript: string; absolute_timestamp: number; relative_timestamp: number }> }>(
      "get_session_transcript",
      { session_id },
    ),
  stopSession: (session_token: string) => call<any>("stop_session", { session_token }),
  sendText: (session_token: string, text: string) => call<any>("send_text", { session_token, text }),
  importElevenLabsVoice: (input: { voice_id: string; display_name?: string }) =>
    call<{
      liveavatar_voice_id: string;
      display_name: string;
      external_voice_id: string;
      secret_id: string;
      preview_url: string | null;
    }>("import_elevenlabs_voice", input),
  getVoicePreview: (voice_id: string) =>
    call<{ audio_base64: string; mime: string }>("get_voice_preview", { voice_id }),
  ttsPreview: (voice_id: string, text: string) =>
    call<{ audio_base64: string; mime: string }>("tts_preview", { voice_id, text }),
};
